// SVG/PNG/XLSX export for FlowchartBuilder, extracted out of the component
// body. Read-only consumer of canvas + Gantt state — no new export logic,
// calls straight through to lib/svgExport.ts and lib/xlsxExport.ts exactly as
// before. Must be wired up AFTER useGanttSchedule() in FlowchartBuilder.tsx,
// since exportXLSX needs getStartDate/getEndDate from that hook's output.
import { buildExportSVGString } from './lib/svgExport';
import { buildTaskExportRows, exportTasksToXlsx } from './lib/xlsxExport';
import type { Box, Arrow, Department, Area } from './types/index';
import type { useGanttSchedule } from './useGanttSchedule';

type GanttScheduleOutput = ReturnType<typeof useGanttSchedule>;

interface UseCanvasExportParams {
  boxes: Box[];
  arrows: Arrow[];
  departments: Department[];
  areas: Area[];
  title: string;
  showLegend: boolean;
  showNotes: boolean;
  showStartDates: boolean;
  showFinishDates: boolean;
  showCriticalPathCanvas: boolean;
  showSubitemBadge: boolean;
  showUidBadge: boolean;
  showAreas: boolean;
  /** Subitem count per parent box ID (see FlowchartBuilder.tsx's subitemCountByBoxId) — needed
   *  here since `boxes` is already filtered to top-level-only by the time it reaches this hook. */
  subitemCountByBoxId: Map<number, number>;
  criticalPath: GanttScheduleOutput['criticalPath'];
  schedulingConflicts: GanttScheduleOutput['schedulingConflicts'];
  getStart: GanttScheduleOutput['getStart'];
  getEnd: GanttScheduleOutput['getEnd'];
  getStartDate: GanttScheduleOutput['getStartDate'];
  getEndDate: GanttScheduleOutput['getEndDate'];
}

export function useCanvasExport(params: UseCanvasExportParams) {
  const {
    boxes, arrows, departments, areas, title, showLegend, showNotes,
    showStartDates, showFinishDates, showCriticalPathCanvas,
    showSubitemBadge, showUidBadge, subitemCountByBoxId, showAreas,
    criticalPath, schedulingConflicts, getStart, getEnd, getStartDate, getEndDate,
  } = params;

  const exportXLSX = (): void => {
    const rows = buildTaskExportRows(boxes, arrows, getStartDate, getEndDate);
    exportTasksToXlsx(rows, title || 'flowchart');
  };

  const exportAs = (format: 'svg' | 'png' | 'xlsx') => {
    if (format === 'xlsx') {
      exportXLSX();
      return;
    }
    const endLabels = new Map<number, string>(boxes.map(b => [b.id, getEnd(b.id)]));
    const startLabels = new Map<number, string>(boxes.map(b => [b.id, getStart(b.id)]));
    const result = buildExportSVGString({
      boxes, arrows, departments, areas,
      showLegend, showNotes,
      showCriticalPath: showCriticalPathCanvas,
      showSubitemBadge, showUidBadge, subitemCountByBoxId, showAreas,
      criticalPath, schedulingConflicts,
      endLabels, startLabels, showStartDates, showFinishDates,
    });
    if (!result) return;
    const { svg, W, H } = result;
    const filename = title || 'flowchart';
    if (format === 'svg') {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${filename}.svg`; a.click();
      URL.revokeObjectURL(url);
    } else {
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const img = new window.Image();
      img.onload = () => {
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = W * scale; canvas.height = H * scale;
        const ctx = canvas.getContext('2d')!;
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, W, H);
        URL.revokeObjectURL(url);
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `${filename}.png`; a.click();
      };
      img.src = url;
    }
  };

  return { exportAs, exportXLSX };
}
